/**
 * ManualAppointmentPanel — Inserimento manuale (a ritroso o futuro) di
 * un appuntamento in GHL scegliendo data, orario, paziente, medico e
 * calendario. Admin only.
 *
 * Flusso:
 *   1) Cerca paziente via autocomplete (`/api/ghl/contacts/search`)
 *   2) Seleziona data + ora inizio (fine = +30 min di default, editabile)
 *   3) Seleziona medico + calendario da dropdown
 *   4) Click "Crea appuntamento" → POST /api/appointments/create-manual
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  CalendarPlus, Loader2, Search, User, CheckCircle2, X, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const API = `${window.location.origin}/api`;

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Combina data (YYYY-MM-DD) + ora (HH:MM) in una ISO string con offset
// Europe/Rome. Non usiamo `new Date(...).toISOString()` perché convertirebbe
// tutto in UTC e perderebbe il timezone dello studio.
function toRomeIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  // Determina se la data è in CEST (UTC+2, ora legale) o CET (UTC+1). In
  // Italia CEST va dall'ultima domenica di marzo all'ultima domenica di
  // ottobre. Facciamo un check semplice via Date.getTimezoneOffset() per
  // il giorno indicato.
  const probe = new Date(`${dateStr}T12:00:00`);
  const offMinutes = -probe.getTimezoneOffset(); // es. 120 per CEST, 60 per CET
  const sign = offMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offMinutes);
  const h = String(Math.floor(abs / 60)).padStart(2, "0");
  const m = String(abs % 60).padStart(2, "0");
  return `${dateStr}T${timeStr}:00${sign}${h}:${m}`;
}

function addMinutes(timeStr, mins) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const total = h * 60 + m + mins;
  const nh = String(Math.floor((total % 1440) / 60)).padStart(2, "0");
  const nm = String(total % 60).padStart(2, "0");
  return `${nh}:${nm}`;
}

export default function ManualAppointmentPanel() {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [date, setDate] = useState(todayIso());
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("09:30");
  const [calendarId, setCalendarId] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [notes, setNotes] = useState("");
  const [calendars, setCalendars] = useState([]);
  const [users, setUsers] = useState([]);
  const [calendarTeam, setCalendarTeam] = useState(null); // Set of user IDs, o null = "tutti"
  const [submitting, setSubmitting] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/appointments/calendars-list`, { withCredentials: true })
      .then((r) => setCalendars(r.data.calendars || []))
      .catch(() => setCalendars([]));
    axios
      .get(`${API}/appointments/users-list`, { withCredentials: true })
      .then((r) => setUsers(r.data.users || []))
      .catch(() => setUsers([]));
  }, []);

  // Quando l'utente cambia calendario, carico il team di quel calendario
  // per filtrare il dropdown medici. Evita l'errore GHL 422 "user not
  // part of calendar team".
  useEffect(() => {
    if (!calendarId) {
      setCalendarTeam(null);
      return;
    }
    axios
      .get(`${API}/appointments/calendar-team/${calendarId}`, { withCredentials: true })
      .then((r) => setCalendarTeam(new Set(r.data.user_ids || [])))
      .catch(() => setCalendarTeam(null));
  }, [calendarId]);

  // Se il medico attualmente selezionato non è nel team del nuovo
  // calendario, deseleziono automaticamente per costringere l'utente a
  // rifare la scelta (evita submit con errore garantito).
  useEffect(() => {
    if (calendarTeam && assignedUserId && !calendarTeam.has(assignedUserId)) {
      setAssignedUserId("");
    }
  }, [calendarTeam, assignedUserId]);

  const availableUsers = useMemo(() => {
    if (!calendarTeam) return users;
    return users.filter((u) => calendarTeam.has(u.id));
  }, [users, calendarTeam]);

  // Debounced live search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!query || query.trim().length < 2 || selectedPatient) {
      setCandidates([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(
          `${API}/ghl/contacts/search?query=${encodeURIComponent(query.trim())}`,
          { withCredentials: true },
        );
        setCandidates(data.contacts || []);
      } catch {
        setCandidates([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [query, selectedPatient]);

  const onStartChange = useCallback((v) => {
    setStartTime(v);
    // auto-shift end +30 min se end è prima o uguale a start
    if (!endTime || endTime <= v) setEndTime(addMinutes(v, 30));
  }, [endTime]);

  const canSubmit = useMemo(() => {
    return (
      selectedPatient?.id &&
      date &&
      startTime &&
      endTime &&
      calendarId &&
      assignedUserId &&
      startTime < endTime
    );
  }, [selectedPatient, date, startTime, endTime, calendarId, assignedUserId]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSubmitting(true);
    setLastCreated(null);
    try {
      const payload = {
        contact_id: selectedPatient.id,
        calendar_id: calendarId,
        assigned_user_id: assignedUserId,
        start_iso: toRomeIso(date, startTime),
        end_iso: toRomeIso(date, endTime),
        title: `${selectedPatient.firstName || ""} ${selectedPatient.lastName || ""}`.trim() || "Appuntamento",
        notes,
      };
      const { data } = await axios.post(
        `${API}/appointments/create-manual`,
        payload,
        { withCredentials: true },
      );
      setLastCreated({
        event_id: data.event_id,
        patient: `${selectedPatient.firstName || ""} ${selectedPatient.lastName || ""}`.trim(),
        date,
        startTime,
      });
      toast.success("Appuntamento creato in GHL");
      // Reset paziente + note, mantieni data/orario/medico/calendario per
      // inserimenti successivi ravvicinati (es. tanti appuntamenti stesso
      // medico stesso giorno).
      setSelectedPatient(null);
      setQuery("");
      setCandidates([]);
      setNotes("");
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Errore creazione appuntamento";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, selectedPatient, calendarId, assignedUserId, date, startTime, endTime, notes]);

  return (
    <div
      className="bg-white rounded-3xl shadow-md p-6 space-y-4 relative z-10"
      data-testid="manual-appointment-panel"
    >
      <div className="border-b border-slate-100 pb-3">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <CalendarPlus className="w-5 h-5 text-emerald-500" />
          Inserimento appuntamento manuale
        </h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Aggiungi un appuntamento in GHL (anche a ritroso) scegliendo data,
          medico, calendario e paziente.
        </p>
      </div>

      {/* Riga 1: paziente autocomplete */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">
          Paziente *
        </label>
        {selectedPatient ? (
          <div
            className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md"
            data-testid="selected-patient"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="flex-1 text-sm font-medium">
              {selectedPatient.firstName} {selectedPatient.lastName}
            </span>
            {selectedPatient.phone && (
              <span className="text-xs text-slate-500 font-mono">
                {selectedPatient.phone}
              </span>
            )}
            <button
              onClick={() => {
                setSelectedPatient(null);
                setQuery("");
              }}
              className="text-slate-400 hover:text-red-500"
              data-testid="clear-patient-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca paziente per nome, telefono o email (min 2 caratteri)"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="patient-search-input"
              />
              {searching && (
                <Loader2 className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />
              )}
            </div>
            {candidates.length > 0 && (
              <div
                className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
                data-testid="patient-search-results"
              >
                {candidates.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedPatient(c)}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-0"
                    data-testid={`patient-result-${c.id}`}
                  >
                    <div className="text-sm font-medium">
                      {c.firstName} {c.lastName}
                    </div>
                    <div className="text-xs text-slate-500">
                      {c.phone || "—"} · {c.email || "—"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Riga 2: data e orario */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">
            Data *
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="appt-date-input"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block flex items-center gap-1">
            <Clock className="w-3 h-3" /> Ora inizio *
          </label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => onStartChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="appt-start-input"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block flex items-center gap-1">
            <Clock className="w-3 h-3" /> Ora fine *
          </label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="appt-end-input"
          />
        </div>
      </div>

      {/* Riga 3: medico e calendario. NB: calendario prima del medico
          perché il team del calendario filtra il dropdown medico. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">
            Calendario *
          </label>
          <select
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="appt-calendar-select"
          >
            <option value="">— Seleziona calendario —</option>
            {calendars.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">
            Medico assegnato *
          </label>
          <select
            value={assignedUserId}
            onChange={(e) => setAssignedUserId(e.target.value)}
            disabled={!calendarId}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400"
            data-testid="appt-doctor-select"
          >
            <option value="">
              {calendarId
                ? availableUsers.length === 0
                  ? "— Nessun medico nel team di questo calendario —"
                  : "— Seleziona medico —"
                : "— Seleziona prima un calendario —"}
            </option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          {calendarId && calendarTeam && (
            <p className="text-xs text-slate-500 mt-1">
              {availableUsers.length} medici disponibili in questo calendario
            </p>
          )}
        </div>
      </div>

      {/* Riga 4: note */}
      <div>
        <label className="text-xs font-medium text-slate-700 mb-1 block">
          Note (opzionale)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Note interne, motivo appuntamento…"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-y"
          data-testid="appt-notes-input"
        />
      </div>

      {/* CTA */}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="submit-manual-appt-btn"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CalendarPlus className="w-4 h-4 mr-2" />
          )}
          Crea appuntamento
        </Button>
        {lastCreated && (
          <div
            className="flex items-center gap-2 text-sm text-emerald-700"
            data-testid="last-created-toast"
          >
            <CheckCircle2 className="w-4 h-4" />
            Creato: <strong>{lastCreated.patient}</strong> · {lastCreated.date}{" "}
            {lastCreated.startTime}
          </div>
        )}
      </div>
    </div>
  );
}
